import { useState } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { apiFetch } from "@/hooks/apiFetch"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { useAppSelector } from "@/lib/hooks"
import { isAdminPortal } from "@/utilities/domain"
import {
  ReportBranchFilter,
  ReportBranchProvider,
  useReportBranch,
} from "@/components/Reports/ReportBranchFilter"

const today = () => {
  const date = new Date()
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

const currentTime = () => {
  const date = new Date()
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
}

const markValues = { present: "1", absent: "0", leave: "L" }

const markValue = (status) => markValues[status] ?? ""

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`

const statusLabels = { present: "Present", absent: "Absent", leave: "Leave" }

const statusLabel = (status) => statusLabels[status] ?? "Unmarked"

// Today offers a one-click stamp of the current time. Any recorded time, and
// any time on another date, is typed so that a late entry can be corrected.
function CheckTimeCell({ member, field, label, isToday, canEdit, onSave }) {
  const value = member[field]
  const needsCheckIn = field === "check_out" && !member.check_in

  if (!canEdit || !["present", undefined, null].includes(member.status)) {
    return value || "—"
  }

  if (!value && isToday) {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={needsCheckIn}
        onClick={() => onSave(currentTime())}
      >
        {label}
      </Button>
    )
  }

  return (
    <TextField
      key={value ?? "empty"}
      size="small"
      type="time"
      aria-label={`${label} for ${member.name}`}
      defaultValue={value ?? ""}
      disabled={needsCheckIn}
      onBlur={(event) => {
        const input = event.target
        if (input.value !== (value ?? "")) {
          onSave(input.value || null, () => {
            input.value = value ?? ""
          })
        }
      }}
    />
  )
}

// The company admin export spans branches, so it names each member's branch.
const downloadCsv = (monthly, dates, withBranch) => {
  const rows = [
    [
      "Staff member",
      ...(withBranch ? ["Branch"] : []),
      "Position",
      ...dates,
      "Present",
      "Absent",
      "Leave",
    ],
    ...monthly.staff.map((member) => [
      member.name,
      ...(withBranch ? [member.branch_name] : []),
      member.position,
      ...dates.map((day) => markValue(member.marks[day])),
      member.present,
      member.absent,
      member.leave,
    ]),
  ]
  const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n")
  const url = URL.createObjectURL(
    new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" })
  )
  const link = document.createElement("a")
  link.href = url
  link.download = `attendance-${monthly.month}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export default function AttendancePage() {
  return (
    <ReportBranchProvider>
      <Attendance />
    </ReportBranchProvider>
  )
}

function Attendance() {
  const permissions = new Set(
    useAppSelector((state) => state.auth.user?.permissions ?? [])
  )
  // The company admin portal reads every branch's attendance; it is marked
  // on the branch portals.
  const adminPortal = isAdminPortal()
  const { branchId } = useReportBranch()
  const queryClient = useQueryClient()
  const [date, setDate] = useState(today)
  const params = new URLSearchParams({ date })
  if (branchId) params.set("branch_id", branchId)
  const { data: attendanceResponse, isLoading } = useCustomQuery(
    ["hr-attendance", date, branchId],
    `staff-attendance?${params.toString()}`
  )
  const attendance = attendanceResponse?.data
  const [month, setMonth] = useState(() => today().slice(0, 7))
  const monthlyParams = new URLSearchParams({ month })
  if (branchId) monthlyParams.set("branch_id", branchId)
  const { data: monthlyResponse, isLoading: monthlyLoading } = useCustomQuery(
    ["hr-attendance", "monthly", month, branchId],
    `staff-attendance/monthly?${monthlyParams.toString()}`,
    { enabled: Boolean(month) }
  )
  const monthly = monthlyResponse?.data
  const dates = Array.from(
    { length: monthly?.days ?? 0 },
    (_, index) => `${monthly.month}-${String(index + 1).padStart(2, "0")}`
  )

  const mark = useMutation({
    mutationFn: ({ staffId, ...mark }) =>
      apiFetch(`staff-attendance/${staffId}/${date}`, {
        method: mark.status ? "PUT" : "DELETE",
        ...(mark.status ? { body: JSON.stringify(mark) } : {}),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["hr-attendance"] }),
    onError: (error) =>
      toast.error(error?.message ?? "Unable to update attendance"),
  })
  const canEditTimes = (member) =>
    !adminPortal && member.is_active && permissions.has("hr-attendance-update")
  // Recording a time means the staff member came in, so it marks them present.
  const saveTime = (member, field) => (time, onError) =>
    mark.mutate(
      { staffId: member.id, status: "present", [field]: time },
      { onError }
    )

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h5">Attendance</Typography>
          <Typography color="text.secondary">
            Daily staff presence by branch. An unmarked day is not counted as
            absent.
          </Typography>
        </Box>
        {permissions.has("hr-staff_members-view") && (
          <Button component={Link} to="/pos/staff" variant="outlined">
            Staff registry
          </Button>
        )}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={2}>
        <TextField
          size="small"
          type="date"
          label="Date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <ReportBranchFilter sx={{ mb: 0 }} />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={2}>
        {[
          ["Present", attendance?.counts?.present ?? 0],
          ["Absent", attendance?.counts?.absent ?? 0],
          ["Leave", attendance?.counts?.leave ?? 0],
          ["Unmarked", attendance?.counts?.unmarked ?? 0],
        ].map(([label, value]) => (
          <Card key={label} variant="outlined" sx={{ minWidth: 135, flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                {label}
              </Typography>
              <Typography variant="h5">{value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Paper variant="outlined">
        {isLoading ? (
          <Typography sx={{ p: 2 }}>Loading attendance...</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Staff member</TableCell>
                {adminPortal && <TableCell>Branch</TableCell>}
                <TableCell>Position</TableCell>
                <TableCell>Attendance</TableCell>
                <TableCell>Check in</TableCell>
                <TableCell>Check out</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(attendance?.staff ?? []).map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    {member.name}
                    {!member.is_active ? " (archived)" : ""}
                  </TableCell>
                  {adminPortal && (
                    <TableCell>{member.branch_name || "—"}</TableCell>
                  )}
                  <TableCell>{member.position || "—"}</TableCell>
                  <TableCell>
                    {adminPortal ? (
                      statusLabel(member.status)
                    ) : (
                      <TextField
                        select
                        size="small"
                        aria-label={`Attendance for ${member.name}`}
                        value={member.status ?? ""}
                        disabled={
                          mark.isPending ||
                          !member.is_active ||
                          !permissions.has("hr-attendance-update")
                        }
                        onChange={(event) =>
                          mark.mutate({
                            staffId: member.id,
                            status: event.target.value,
                          })
                        }
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem
                          value=""
                          disabled={!permissions.has("hr-attendance-delete")}
                        >
                          Unmarked
                        </MenuItem>
                        <MenuItem value="present">Present</MenuItem>
                        <MenuItem value="absent">Absent</MenuItem>
                        <MenuItem value="leave">Leave</MenuItem>
                      </TextField>
                    )}
                  </TableCell>
                  <TableCell>
                    <CheckTimeCell
                      member={member}
                      field="check_in"
                      label="Check in"
                      isToday={date === today()}
                      canEdit={canEditTimes(member)}
                      onSave={saveTime(member, "check_in")}
                    />
                  </TableCell>
                  <TableCell>
                    <CheckTimeCell
                      member={member}
                      field="check_out"
                      label="Check out"
                      isToday={date === today()}
                      canEdit={canEditTimes(member)}
                      onSave={saveTime(member, "check_out")}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {!attendance?.staff?.length && (
                <TableRow>
                  <TableCell colSpan={adminPortal ? 6 : 5}>
                    No staff for this date.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
        mt={4}
        mb={2}
      >
        <Box>
          <Typography variant="h6">Monthly attendance</Typography>
          <Typography color="text.secondary" variant="body2">
            1 is present, 0 is absent, L is leave and a blank day is unmarked.
            Select a day number to {adminPortal ? "show" : "mark"} that date
            above.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
          <TextField
            size="small"
            type="month"
            label="Month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            disabled={!monthly?.staff?.length}
            onClick={() => downloadCsv(monthly, dates, adminPortal)}
          >
            Export
          </Button>
        </Stack>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        {monthlyLoading ? (
          <Typography sx={{ p: 2 }}>Loading monthly attendance...</Typography>
        ) : (
          <Table size="small" sx={{ "& td, & th": { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    bgcolor: "background.paper",
                    minWidth: 160,
                  }}
                >
                  Staff member
                </TableCell>
                {adminPortal && <TableCell>Branch</TableCell>}
                {dates.map((day) => (
                  <TableCell
                    key={day}
                    align="center"
                    aria-label={`Show ${day}`}
                    onClick={() => setDate(day)}
                    sx={{
                      cursor: "pointer",
                      fontWeight: day === date ? 700 : 400,
                      color: day === date ? "primary.main" : "inherit",
                    }}
                  >
                    {Number(day.slice(8))}
                  </TableCell>
                ))}
                <TableCell align="right">Present</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">Leave</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(monthly?.staff ?? []).map((member) => (
                <TableRow key={member.id}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      bgcolor: "background.paper",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {member.name}
                    {!member.is_active ? " (archived)" : ""}
                  </TableCell>
                  {adminPortal && (
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {member.branch_name || "—"}
                    </TableCell>
                  )}
                  {dates.map((day) => (
                    <TableCell
                      key={day}
                      align="center"
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        color:
                          member.marks[day] === "absent"
                            ? "error.main"
                            : "inherit",
                      }}
                    >
                      {markValue(member.marks[day])}
                    </TableCell>
                  ))}
                  <TableCell align="right">{member.present}</TableCell>
                  <TableCell align="right">{member.absent}</TableCell>
                  <TableCell align="right">{member.leave}</TableCell>
                </TableRow>
              ))}
              {!monthly?.staff?.length && (
                <TableRow>
                  <TableCell colSpan={dates.length + (adminPortal ? 5 : 4)}>
                    No staff for this month.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  )
}
